/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import { func, object, string } from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { useUIDSeed } from 'react-uid';

import BlockHeading from '../../../../ContentTypeComponents/BlockHeading';
import parseFontFamilyExtension from '../../../../../helpers/contentstack/parseFontFamilyExtension';
import noop from '../../../../../helpers/noop';
import ProductImage from '../PersonalizedHomePageProduct/Partials/ProductImage';

const useStyles = makeStyles((theme) => ({
    root: (styles) => ({
        padding: styles?.containerPadding || '32px 0px',
        [theme.breakpoints.down(600)]: {
            padding: styles?.containerPadding || '24px 0px',
        },
    }),

    titleContainer: (styles) => ({
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 10px',

        [theme.breakpoints.up(1024)]: {
            paddingLeft: '20px',
            paddingRight: '20px',
        },

        [theme.breakpoints.up(1400)]: {
            paddingLeft: 0,
            paddingRight: 0,
        },
        '& h2': {
            color: styles?.titleFontColor || '#000000',
            margin: styles?.titleMargin || '0px 18px 16px',
            padding: styles?.padding || '0px',
            fontFamily: parseFontFamilyExtension(styles?.titleFontFamily) || theme?.typography?.fontFamily,
            textAlign: styles?.textAlign || 'center',
            fontSize: styles?.titleFontSize || '28px',
            fontWeight: styles?.fontWeight || '600',
            lineHeight: '35px',
            letterSpacing: '0.01em',
            textUnderlinePosition: 'from-font',
            textDecorationSkipInk: 'none',
            [theme.breakpoints.down(600)]: {
                fontSize: styles?.titleFontSize || '20px',
                lineHeight: '28px',
            },
        },
    }),
    productContainer: ({ noOfProductsToShow }) => ({
        margin: '0 auto',
        padding: '0 10px',
        maxWidth: '1400px',
        display: 'grid',
        gridTemplateColumns: `repeat(${noOfProductsToShow || 2}, 1fr)`,
        gap: '18px',
        [theme.breakpoints.up(600)]: {
            gridTemplateColumns: 'repeat(3, 1fr)',

        },
        [theme.breakpoints.up(768)]: {
            gap: '48px',
            gridTemplateColumns: 'repeat(4, 1fr)',
        },

        [theme.breakpoints.up(1024)]: {
            gridTemplateColumns: `repeat(${noOfProductsToShow || 5}, 1fr)`,
        },

    }),
    link: {
        textDecoration: 'none',
        cursor: 'pointer',
    },
    firstLink: {
        display: 'inline-block',
        width: '100%',
    },
    stackedLabel: {
        display: 'block',
        paddingTop: '8px',
        [theme.breakpoints.down(600)]: {
            paddingTop: '16px',
        },
    },
    circle: (styles) => ({
        width: '100%',
        aspectRatio: '1 / 1',
        borderRadius: '50%',
        overflow: 'hidden',
        backgroundColor: styles?.backgroundColor,
    }),
    imgDiv: {
        position: 'relative',
        '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
        },
    },
    nested: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    circleLabel: (styles) => ({
        textAlign: 'center',
        textWrap: 'auto',
        padding: '0 2px',
        color: styles?.labelColor,
        fontSize: styles.labelFontSize || '24px',
        fontFamily: parseFontFamilyExtension(styles?.labelFontFamily),
        fontStyle: styles.labelFontStyle,
        fontWeight: styles?.labelFontWeight,
    }),
}));

function CollectionGridUI({ data, title, trackClickThrough }) {
    const seed = useUIDSeed();

    const labelStyles = data?.content?.circle_label_styling;
    const titleStyles = data?.content?.title_styling;
    const isNested = data?.content?.align_circle_label === 'Nested';
    const isShowImage = data?.content?.select_collection_properties?.includes('Show Image Only');
    const maxProductsToShow = (data?.content?.number_of_rows_to_display || 1) * (Math.floor(data?.content?.number_of_products_per_row) || 6);

    const classes = useStyles({
        labelColor: labelStyles?.label_font_color?.color,
        labelFontSize: labelStyles?.label_font_size,
        labelFontStyle: labelStyles?.label_font_style,
        labelFontFamily: labelStyles?.label_font_family,
        labelFontWeight: labelStyles?.font_weight,
        backgroundColor: isShowImage ? '' : data?.content?.collection_item_color?.color,
        noOfProductsToShow: Math.floor(data?.content?.number_of_products_per_row),
        titleFontColor: titleStyles?.title_font_color?.color,
        titleFontFamily: titleStyles?.title_font_family,
        titleFontStyle: titleStyles?.title_font_style,
        titleFontSize: titleStyles?.title_font_size,
        padding: titleStyles?.padding,
        textAlign: titleStyles?.align_title,
        titleMargin: titleStyles?.title_margin,
        fontWeight: titleStyles?.font_weight,
        containerPadding: data?.content?.container_padding,
    });

    const renderNameOfCollection = (name) => {
        if (data?.content?.select_collection_properties?.includes('Product Title')) {
            return <div className={classes.circleLabel}>{name}</div>;
        }
        return '';
    };

    return (
        <div className={classes.root} data-testid="CollectionGridUI">
            <div className={classes.titleContainer}>
                <BlockHeading heading={title} />
            </div>
            <div className={`${classes.productContainer} `}>
                {
                    data?.collections?.slice(0, maxProductsToShow).map((collectionObj) => {
                        const {
                            name, image, path, id,
                        } = collectionObj;

                        return (
                            <div
                                key={seed(collectionObj)}
                                className={classes.productCard}
                                onClick={() => trackClickThrough(id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={() => { }}
                            >
                                <Link to={path} className={`${classes.link} ${classes.firstLink}`}>
                                    {
                                        isShowImage
                                            ? (
                                                <div className={`${classes.circle} ${classes.imgDiv}`}>
                                                    <ProductImage image={image} />
                                                </div>
                                            )
                                            : (
                                                <div className={`${classes.circle} ${isNested ? classes.nested : ''}`}>
                                                    {isNested && renderNameOfCollection(name)}
                                                </div>
                                            )
                                    }

                                </Link>
                                {
                                    !isNested && (
                                        <Link to={path} className={`${classes.link} ${classes.stackedLabel}`}>
                                            {renderNameOfCollection(name)}
                                        </Link>
                                    )
                                }

                            </div>
                        );
                    })

                }
            </div>
        </div>
    );
}

CollectionGridUI.propTypes = {
    data: object.isRequired,
    title: string.isRequired,
    trackClickThrough: func,
};

CollectionGridUI.defaultProps = {
    trackClickThrough: noop,
};

export default CollectionGridUI;
